import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, XAxis, YAxis,
} from 'recharts';
import {
  ActivityIcon, AlertTriangleIcon, CheckCircle2Icon, InboxIcon, ListIcon,
  PieChartIcon, UsersIcon, XCircleIcon,
} from 'lucide-react';

import { api, type DashboardAggregate } from '@/lib/api';
import { useContent, useDimensionLabel, useLabel, usePercent, useT, useTn } from '@/lib/i18n';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useModuleName } from '@/hooks/use-module-name';
import { DimensionSpread } from '@/features/analysis/distribution';
import {
  ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

/**
 * Dashboard (build/05 §B, build/07 Phase 7).
 *
 * Every number here comes from `GET /results/aggregate` — one `$facet` pipeline
 * over `results` plus one `$group` over `assessmentinstances`, with no
 * `$lookup` in either. Phase 0 shipped this screen with hard-coded zeroes
 * because inventing numbers on a stakeholder console turns a demo into a false
 * claim; those zeroes are now real.
 *
 * The dimension chart is single-series per module — one measure across the
 * dimensions of one instrument — so no categorical palette applies and it uses
 * the design system's own chart token, like every other chart here.
 */

const ALL = '__all__';

function Tile({
  label, value, hint, icon: Icon, tint,
}: {
  label: string; value: string; hint: string;
  icon: typeof ActivityIcon;
  /** Token pair for the icon tile — the walkthrough tints each one differently. */
  tint: { bg: string; fg: string };
}) {
  return (
    <Card className="p-[18px]">
      {/* Horizontal, not stacked: the walkthrough's tile is an icon beside the
          number, which is both shorter and easier to scan than a header block
          above a body block. */}
      <div className="flex items-start gap-3.5">
        <div className="kpi-icon" style={{ background: tint.bg, color: tint.fg }}>
          <Icon />
        </div>
        <div className="min-w-0">
          <p className="text-muted-foreground text-[13px]">{label}</p>
          <p className="mt-0.5 text-[25px] font-bold tracking-[-0.02em] leading-none">{value}</p>
          <p className="text-muted-foreground mt-1.5 text-xs">{hint}</p>
        </div>
      </div>
    </Card>
  );
}

/** Tints, from the walkthrough's per-tile `style="background:…;color:…"`. */
const TINTS = {
  primary: { bg: 'var(--primary-soft, var(--accent))', fg: 'var(--primary)' },
  good: { bg: 'var(--status-good-soft, var(--accent))', fg: 'var(--status-good, var(--primary))' },
  gold: { bg: 'var(--brand-gold-soft, var(--accent))', fg: 'var(--brand-gold, var(--primary))' },
  crit: { bg: 'var(--status-crit-soft, var(--accent))', fg: 'var(--status-crit, var(--destructive))' },
};

export function Dashboard() {
  const t = useT();
  const tn = useTn();
  const content = useContent();
  const label = useLabel();
  const moduleName = useModuleName();
  const percent = usePercent();
  const dimensionLabel = useDimensionLabel();
  const [moduleId, setModuleId] = useState(ALL);
  const [shareView, setShareView] = useState<'chart' | 'list'>('chart');

  // Inside the component because the legend label is a translated string, and a
  // module-scope constant would freeze whichever language loaded first.
  const chartConfig = {
    average: { label: t('dashboard.average'), color: 'var(--chart-1)' },
  } satisfies ChartConfig;

  const trendConfig = {
    completions: { label: t('dashboard.completions'), color: 'var(--chart-1)' },
  } satisfies ChartConfig;

  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules });
  const params = moduleId === ALL ? {} : { moduleId };
  const agg = useQuery({
    queryKey: ['aggregate', params],
    queryFn: () => api.aggregate(params),
    refetchInterval: 60_000,
  });

  const data: DashboardAggregate | undefined = agg.data;
  // Optional all the way down: a response missing `instances` should render
  // the empty state, not white-screen the dashboard.
  const byState = data?.instances?.byState ?? {};
  const completed = byState.completed ?? 0;
  const inFlight = (byState.in_progress ?? 0) + (byState.ready ?? 0);
  const instanceTotal = data?.instances?.total ?? 0;
  const completionRate = instanceTotal > 0
    ? percent(Math.round((completed / instanceTotal) * 100))
    : '—';
  const flagged = (data?.qcFlags ?? []).reduce((n, f) => n + f.count, 0);

  // The chart shows one module at a time: dimension means from different
  // instruments are different measures, and putting them on one axis would be
  // the dual-axis mistake wearing a disguise.
  const chartModule = moduleId === ALL ? data?.byDimension[0]?.moduleId : moduleId;
  /**
   * Share of scored results per module — categorical, one hue per MODULE.
   *
   * Colour follows the entity, not its rank: the fill is keyed off the module's
   * position in the catalogue, so filtering or reordering cannot repaint the
   * survivors. The three hues are the walkthrough's own, validated with the
   * dataviz skill's script (worst adjacent CVD ΔE 9.2 deutan).
   */

  const moduleShare = (data?.byModule ?? []).map((m, i) => ({
    name: moduleName(m.moduleId),
    value: m.results,
    fill: `var(--chart-${(i % 5) + 1})`,
  }));

  const shareConfig = Object.fromEntries(
    moduleShare.map((m, i) => [m.name, { label: m.name, color: `var(--chart-${(i % 5) + 1})` }]),
  ) satisfies ChartConfig;

  /**
   * The rows for the chart AND for the spread beneath it, taken once.
   *
   * M2 hangs off the same slice deliberately: the mean and its spread must
   * describe the same cohort, and two filters that could drift apart is exactly
   * how a dashboard ends up reporting a spread for a different module than the
   * mean above it.
   */
  const moduleDimensions = (data?.byDimension ?? []).filter((d) => d.moduleId === chartModule);

  const chartData = moduleDimensions
    // The aggregate carries the KEY and no label, so this axis used to read
    // `emotionalStability`. `dimensionLabel` translates the ones we know and
    // leaves an unknown key legible rather than blank.
    .map((d) => ({ name: dimensionLabel(d.key), average: d.average }));

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={t('nav.dashboard')}
        description={t('dashboard.description')}
        actions={
          <Select value={moduleId} onValueChange={setModuleId}>
            <SelectTrigger className="w-56" aria-label={t('dashboard.filterByModule')}>
              <SelectValue placeholder={t('dashboard.allModules')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('dashboard.allModules')}</SelectItem>
              {(modules.data?.items ?? []).map((m) => (
                // The module's NAME is catalogue content, so it follows the
                // operator's language through `content()` rather than being
                // pinned to `.en` as it was.
                <SelectItem key={m.moduleId} value={m.moduleId}>{content(m.name)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      {agg.isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-32 w-full" />)}
        </div>
      )}

      {agg.error && (
        <EmptyState
          icon={XCircleIcon}
          title={t('dashboard.error')}
          description={agg.error instanceof Error ? agg.error.message : t('table.errorDescription')}
        />
      )}

      {data && instanceTotal === 0 && (
        <EmptyState
          icon={InboxIcon}
          title={t('dashboard.empty.title')}
          description={t('dashboard.empty.description')}
          actions={
            <Button asChild>
              <Link to="/take">{t('nav.take')}</Link>
            </Button>
          }
        />
      )}

      {data && instanceTotal > 0 && (
        <>
          <div className="mb-5 grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
            <Tile
              label={t('dashboard.completions')}
              value={String(completed)}
              // `tn`, not a ternary on `=== 1`: Arabic has six plural
              // categories, and the two-branch version silently produces
              // nonsense in four of them.
              hint={tn('dashboard.scoredResults', data.results)}
              icon={CheckCircle2Icon}
              tint={TINTS.good}
            />
            <Tile
              label={t('nav.candidates')}
              value={String(data.candidates)}
              hint={tn('dashboard.inFlight', inFlight)}
              icon={UsersIcon}
              tint={TINTS.primary}
            />
            <Tile
              label={t('dashboard.completionRate')}
              value={completionRate}
              hint={t('dashboard.completionRate.hint')}
              icon={ActivityIcon}
              tint={TINTS.primary}
            />
            <Tile
              label={t('dashboard.qualityFlags')}
              value={String(flagged)}
              hint={
                data.qcFlags.length
                  ? data.qcFlags.map((f) => `${label('qcFlag', f.flag)} ${percent(f.rate, 1)}`).join(' · ')
                  : t('dashboard.noFlags')
              }
              icon={AlertTriangleIcon}
              tint={TINTS.gold}
            />
          </div>

          {/*
            COMPLETIONS OVER TIME — the last chart the walkthrough asked for
            (`plan-beta` E). It reads a new branch of the SAME `$facet`, not a
            second pipeline: the dashboard's contract is one aggregation over
            results plus one over instances, and a trend line was not worth
            breaking it for.

            Hidden below two weeks of data, because a line through one point is
            not a trend — it is a dot that implies one.
          */}
          {(data.byWeek?.length ?? 0) > 1 && (
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-base">{t('dashboard.overTime')}</CardTitle>
                <CardDescription>{t('dashboard.overTime.hint')}</CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={trendConfig} className="max-h-56 w-full">
                  <LineChart data={data.byWeek} margin={{ left: 8, right: 16, top: 8 }}>
                    <CartesianGrid vertical={false} stroke="var(--border)" />
                    <XAxis
                      dataKey="week"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
                    />
                    <YAxis
                      allowDecimals={false}
                      width={32}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line
                      dataKey="completions"
                      type="monotone"
                      stroke="var(--color-completions)"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ChartContainer>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('dashboard.byDimension')}</CardTitle>
                <CardDescription>
                  {t('dashboard.byDimension.hint', { module: moduleName(chartModule) || t('dashboard.noModule') })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {chartData.length === 0 ? (
                  <p className="text-muted-foreground text-sm">{t('dashboard.noResultsForModule')}</p>
                ) : (
                  <ChartContainer config={chartConfig} className="max-h-72 w-full">
                    {/* Physical margins, and deliberately so: the plot is
                        pinned `ltr` by `ChartContainer` in both languages. */}
                    <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                      <CartesianGrid horizontal={false} stroke="var(--border)" />
                      <XAxis
                        type="number"
                        domain={[0, 100]}
                        tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={150}
                        tickLine={false}
                        axisLine={false}
                        tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
                      />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="average" fill="var(--color-average)" radius={[0, 4, 4, 0]} barSize={18} />
                    </BarChart>
                  </ChartContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex-row items-start justify-between gap-2 space-y-0">
                <div>
                  <CardTitle className="text-base">{t('dashboard.byModule')}</CardTitle>
                  <CardDescription>{t('dashboard.byModule.hint')}</CardDescription>
                </div>
                {/* The walkthrough shows this as a donut. A chart is the better
                    read for share-of-total; the list is the better read for exact
                    counts — so the card carries both rather than picking one and
                    being wrong half the time. */}
                <div
                  className="flex gap-1 rounded-md border p-0.5"
                  role="group"
                  aria-label={t('dashboard.viewMode')}
                >
                  {(['chart', 'list'] as const).map((mode) => (
                    <Button
                      key={mode}
                      size="sm"
                      variant={shareView === mode ? 'secondary' : 'ghost'}
                      className="h-7 px-2"
                      aria-pressed={shareView === mode}
                      onClick={() => setShareView(mode)}
                    >
                      {mode === 'chart' ? <PieChartIcon className="size-4" /> : <ListIcon className="size-4" />}
                      <span className="sr-only">
                        {t(mode === 'chart' ? 'dashboard.viewDonut' : 'dashboard.viewList')}
                      </span>
                    </Button>
                  ))}
                </div>
              </CardHeader>
              <CardContent>
                {moduleShare.length === 0 ? (
                  <p className="text-muted-foreground text-sm">{t('dashboard.noResults')}</p>
                ) : shareView === 'chart' ? (
                  <ChartContainer config={shareConfig} className="mx-auto aspect-square max-h-64">
                    <PieChart>
                      <ChartTooltip content={<ChartTooltipContent nameKey="name" />} />
                      {/* cutout 62% in the walkthrough; a 2px surface ring
                          between slices, which the dataviz skill asks for. */}
                      <Pie
                        data={moduleShare}
                        dataKey="value"
                        nameKey="name"
                        innerRadius="62%"
                        outerRadius="100%"
                        paddingAngle={2}
                        stroke="var(--card)"
                        strokeWidth={2}
                      >
                        {moduleShare.map((slice) => (
                          <Cell key={slice.name} fill={slice.fill} />
                        ))}
                      </Pie>
                      <ChartLegend content={<ChartLegendContent nameKey="name" />} />
                    </PieChart>
                  </ChartContainer>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('dashboard.column.module')}</TableHead>
                        <TableHead>{t('dashboard.column.state')}</TableHead>
                        <TableHead className="w-20 text-end">{t('dashboard.column.count')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(data.instances?.byModule ?? []).map((row) => (
                        <TableRow key={`${row.moduleId}:${row.state}`}>
                          <TableCell>{row.moduleId}</TableCell>
                          <TableCell>
                            <Badge variant={row.state === 'completed' ? 'default' : 'secondary'}>
                              {label('state', row.state)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-end font-medium">{row.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>

          {/**
            * M2 — the spread beside the mean it qualifies.
            *
            * Directly under the dimension chart, because the two are one
            * reading: the bars above say where each dimension sits and this says
            * whether that middle describes anybody. A dashboard that shows only
            * the first reports a bimodal dimension as an average person.
            */}
          <div className="mt-4">
            <DimensionSpread rows={moduleDimensions} />
          </div>
        </>
      )}
    </div>
  );
}
